import type { HubSupportTicketMessage } from "@/components/HubSupportTicketMessageThread";
import { apiFetch } from "@/lib/api";

export const SAFETY_REPORT_KINDS = ["concern", "near_miss", "incident"] as const;
export type SafetyReportKind = (typeof SAFETY_REPORT_KINDS)[number];

export const SAFETY_REPORT_SEVERITIES = ["low", "medium", "high", "critical"] as const;
export type SafetyReportSeverity = (typeof SAFETY_REPORT_SEVERITIES)[number];

export const SAFETY_REPORT_CATEGORIES = [
  "hazard_unsafe_condition",
  "equipment_machinery",
  "electrical_laser",
  "chemical_fumes",
  "ergonomics_lifting",
  "personal_protective_equipment",
  "slip_trip_fall",
  "cut_burn_puncture",
  "material_handling",
  "other",
] as const;
export type SafetyReportCategory = (typeof SAFETY_REPORT_CATEGORIES)[number];

export const SAFETY_REPORT_STATUSES = [
  "open",
  "investigating",
  "corrective_action",
  "resolved",
  "closed",
] as const;
export type SafetyReportStatus = (typeof SAFETY_REPORT_STATUSES)[number];

export const SAFETY_MEDICAL_ATTENTION_LEVELS = [
  "none",
  "first_aid",
  "clinic_urgent_care",
  "emergency_room",
  "refused_treatment",
] as const;
export type SafetyMedicalAttentionLevel = (typeof SAFETY_MEDICAL_ATTENTION_LEVELS)[number];

export const SAFETY_REPORT_KIND_LABELS: Record<SafetyReportKind, string> = {
  concern: "Safety concern",
  near_miss: "Near miss",
  incident: "Incident / Injury",
};

export const SAFETY_REPORT_SEVERITY_LABELS: Record<SafetyReportSeverity, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

export const SAFETY_REPORT_CATEGORY_LABELS: Record<SafetyReportCategory, string> = {
  hazard_unsafe_condition: "Unsafe condition / housekeeping",
  equipment_machinery: "Equipment / machinery / tooling",
  electrical_laser: "Electrical / bench test / laser",
  chemical_fumes: "Chemical / potting / fumes",
  ergonomics_lifting: "Ergonomics / lifting",
  personal_protective_equipment: "Personal protective equipment",
  slip_trip_fall: "Slip, trip, or fall",
  cut_burn_puncture: "Cut, burn, pinch, or puncture",
  material_handling: "Warehouse / dock / material handling",
  other: "Other",
};

export const SAFETY_REPORT_STATUS_LABELS: Record<SafetyReportStatus, string> = {
  open: "Open",
  investigating: "Investigating",
  corrective_action: "Corrective action",
  resolved: "Resolved",
  closed: "Closed",
};

export const SAFETY_MEDICAL_ATTENTION_LABELS: Record<SafetyMedicalAttentionLevel, string> = {
  none: "No injury / no medical treatment",
  first_aid: "On-site first aid",
  clinic_urgent_care: "Clinic / urgent care / physician",
  emergency_room: "Emergency room / ambulance",
  refused_treatment: "Treatment offered and declined",
};

export const SAFETY_WORK_AREA_OPTIONS = [
  { value: "Picking", label: "Picking" },
  { value: "Configuration", label: "Configuration" },
  { value: "Laser Mark", label: "Laser Mark" },
  { value: "Benches / Cal+Test", label: "Benches / Cal+Test" },
  { value: "SIM", label: "SIM" },
  { value: "Assembly", label: "Assembly" },
  { value: "Pre-Encapsulation", label: "Pre-Encapsulation" },
  { value: "Encapsulation", label: "Encapsulation" },
  { value: "Final QC", label: "Final QC" },
  { value: "Clamp QC", label: "Clamp QC" },
  { value: "Receiving / Dock", label: "Receiving / Dock" },
  { value: "Holding / To Stock", label: "Holding / To Stock" },
  { value: "Pre-Pack / Outbound", label: "Pre-Pack / Outbound" },
  { value: "Warehouse", label: "Warehouse" },
  { value: "Office / Breakroom", label: "Office / Breakroom" },
  { value: "Facility / Grounds", label: "Facility / Grounds" },
  { value: "Other", label: "Other" },
] as const;

export function safetyReportKindTone(
  kind: SafetyReportKind,
): "warning" | "neutral" | "danger" {
  if (kind === "incident") return "danger";
  if (kind === "near_miss") return "warning";
  return "neutral";
}

export function safetyReportSeverityTone(
  severity: SafetyReportSeverity,
): "neutral" | "warning" | "danger" {
  if (severity === "critical" || severity === "high") return "danger";
  if (severity === "medium") return "warning";
  return "neutral";
}

export function safetyReportStatusTone(
  status: SafetyReportStatus,
): "warning" | "neutral" | "success" {
  if (status === "open") return "warning";
  if (status === "resolved" || status === "closed") return "success";
  return "neutral";
}

export function safetyReportStatusIsTerminal(status: SafetyReportStatus): boolean {
  return status === "resolved" || status === "closed";
}

export interface SafetyReportRow {
  id: string;
  report_kind: SafetyReportKind;
  severity: SafetyReportSeverity;
  category: SafetyReportCategory;
  status: SafetyReportStatus;
  title: string;
  work_area: string;
  occurred_at: string;
  description: string;
  immediate_action_taken?: string | null;
  suggested_prevention?: string | null;
  equipment_involved?: string | null;
  witnesses?: string | null;
  injured_person_name?: string | null;
  medical_attention_level: SafetyMedicalAttentionLevel;
  osha_recordable?: boolean;
  pending_feedback: boolean;
  reported_by_user_id?: string;
  assigned_to_user_id?: string | null;
  containment_action?: string | null;
  root_cause?: string | null;
  corrective_action?: string | null;
  resolution_note?: string | null;
  resolved_at?: string | null;
  resolved_by_user_id?: string | null;
  created_at: string;
  updated_at: string;
  reported_by_display_name?: string | null;
  assigned_to_display_name?: string | null;
  resolved_by_display_name?: string | null;
  message_count?: number;
}

export interface SafetyReportMessage {
  id: string;
  body: string;
  is_staff: boolean;
  created_at: string;
  author_display_name: string | null;
}

export interface SafetyReportAssigneeOption {
  id: string;
  display_name: string;
}

export function safetyReportMessagesAsHubThread(
  messages: readonly SafetyReportMessage[],
): HubSupportTicketMessage[] {
  return messages.map((message) => ({
    id: message.id,
    created_at: message.created_at,
    author_user_id: "",
    author_email: "",
    author_display_name:
      message.author_display_name ?? (message.is_staff ? "Safety team" : "Reporter"),
    body: message.body,
    is_from_reporter: !message.is_staff,
    attachments: [],
  }));
}

export const PERSONAL_SAFETY_REPORTS_QUERY_KEY = ["personal", "safety-reports"] as const;
export const MANUFACTURING_SAFETY_REPORTS_QUERY_KEY = ["manufacturing", "safety-reports"] as const;
export const MANUFACTURING_SAFETY_REPORT_ASSIGNEES_QUERY_KEY = [
  "manufacturing",
  "safety-reports",
  "assignees",
] as const;

export async function fetchMySafetyReports(): Promise<{ rows: SafetyReportRow[] }> {
  return apiFetch<{ rows: SafetyReportRow[] }>("/api/personal/safety-reports/mine");
}

export async function fetchMySafetyReportDetail(
  reportId: string,
): Promise<{ report: SafetyReportRow; messages: SafetyReportMessage[] }> {
  return apiFetch<{ report: SafetyReportRow; messages: SafetyReportMessage[] }>(
    `/api/personal/safety-reports/${encodeURIComponent(reportId)}`,
  );
}

export interface CreateSafetyReportInput {
  report_kind: SafetyReportKind;
  severity: SafetyReportSeverity;
  category: SafetyReportCategory;
  title: string;
  work_area: string;
  occurred_at: string;
  description: string;
  immediate_action_taken?: string | null;
  suggested_prevention?: string | null;
  equipment_involved?: string | null;
  witnesses?: string | null;
  injured_person_name?: string | null;
  medical_attention_level: SafetyMedicalAttentionLevel;
}

export async function createSafetyReport(
  input: CreateSafetyReportInput,
): Promise<{ ok: boolean; id: string }> {
  return apiFetch<{ ok: boolean; id: string }>("/api/personal/safety-reports", {
    method: "POST",
    body: input,
  });
}

export async function postMySafetyReportMessage(
  reportId: string,
  body: string,
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    `/api/personal/safety-reports/${encodeURIComponent(reportId)}/messages`,
    {
      method: "POST",
      body: { body },
    },
  );
}

export interface FetchManufacturingSafetyReportsParams {
  status?: string;
  reportKind?: string;
  severity?: string;
  workArea?: string;
  pendingFeedback?: boolean;
  oshaRecordable?: boolean;
  search?: string;
  limit?: number;
}

export async function fetchManufacturingSafetyReports(
  parameters: FetchManufacturingSafetyReportsParams = {},
): Promise<{ rows: SafetyReportRow[] }> {
  const query = new URLSearchParams();
  if (parameters.status) query.set("status", parameters.status);
  if (parameters.reportKind) query.set("report_kind", parameters.reportKind);
  if (parameters.severity) query.set("severity", parameters.severity);
  if (parameters.workArea) query.set("work_area", parameters.workArea);
  if (parameters.pendingFeedback) query.set("pending_feedback", "1");
  if (parameters.oshaRecordable) query.set("osha_recordable", "1");
  if (parameters.search?.trim()) query.set("search", parameters.search.trim());
  if (parameters.limit != null) query.set("limit", String(parameters.limit));
  const suffix = query.toString() !== "" ? `?${query.toString()}` : "";
  return apiFetch<{ rows: SafetyReportRow[] }>(`/api/manufacturing/safety-reports${suffix}`);
}

export async function fetchManufacturingSafetyReportDetail(
  reportId: string,
): Promise<{ report: SafetyReportRow; messages: SafetyReportMessage[] }> {
  return apiFetch<{ report: SafetyReportRow; messages: SafetyReportMessage[] }>(
    `/api/manufacturing/safety-reports/${encodeURIComponent(reportId)}`,
  );
}

export async function fetchManufacturingSafetyReportAssignees(): Promise<{
  users: SafetyReportAssigneeOption[];
}> {
  return apiFetch<{ users: SafetyReportAssigneeOption[] }>(
    "/api/manufacturing/safety-reports/assignees",
  );
}

export interface PatchManufacturingSafetyReportBody {
  status?: SafetyReportStatus;
  severity?: SafetyReportSeverity;
  category?: SafetyReportCategory;
  medical_attention_level?: SafetyMedicalAttentionLevel;
  osha_recordable?: boolean;
  pending_feedback?: boolean;
  assigned_to_user_id?: string | null;
  containment_action?: string | null;
  root_cause?: string | null;
  corrective_action?: string | null;
  resolution_note?: string | null;
}

export async function patchManufacturingSafetyReport(
  reportId: string,
  body: PatchManufacturingSafetyReportBody,
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    `/api/manufacturing/safety-reports/${encodeURIComponent(reportId)}`,
    {
      method: "PATCH",
      body,
    },
  );
}

export async function postManufacturingSafetyReportMessage(
  reportId: string,
  body: string,
  requestFeedback = false,
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    `/api/manufacturing/safety-reports/${encodeURIComponent(reportId)}/messages`,
    {
      method: "POST",
      body: { body, request_feedback: requestFeedback },
    },
  );
}

function escapeCsvCell(value: string | number | boolean | null | undefined): string {
  if (value == null) return "";
  const text = String(value);
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function downloadSafetyReportsCsv(rows: readonly SafetyReportRow[]): void {
  const headers = [
    "Report ID",
    "Type",
    "Severity",
    "Category",
    "Status",
    "Title",
    "Work Area",
    "Occurred At",
    "Reported By",
    "Assigned To",
    "Injured Person",
    "Medical Attention",
    "OSHA Recordable",
    "Description",
    "Immediate Action Taken",
    "Containment Action",
    "Root Cause",
    "Corrective Action",
    "Resolution Summary",
    "Created At",
    "Resolved At",
  ];
  const lines = [headers.join(",")];
  for (const row of rows) {
    lines.push(
      [
        row.id,
        SAFETY_REPORT_KIND_LABELS[row.report_kind],
        SAFETY_REPORT_SEVERITY_LABELS[row.severity],
        SAFETY_REPORT_CATEGORY_LABELS[row.category],
        SAFETY_REPORT_STATUS_LABELS[row.status],
        row.title,
        row.work_area,
        row.occurred_at,
        row.reported_by_display_name ?? "",
        row.assigned_to_display_name ?? "",
        row.injured_person_name ?? "",
        SAFETY_MEDICAL_ATTENTION_LABELS[row.medical_attention_level],
        row.osha_recordable ? "Yes" : "No",
        row.description,
        row.immediate_action_taken ?? "",
        row.containment_action ?? "",
        row.root_cause ?? "",
        row.corrective_action ?? "",
        row.resolution_note ?? "",
        row.created_at,
        row.resolved_at ?? "",
      ]
        .map((cell) => escapeCsvCell(cell))
        .join(","),
    );
  }
  const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `safety-reports-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
